/* eslint-disable camelcase */
// @noflow
import moment from 'moment';
import BaseEventListing from './BaseEventListing';

import type { Franchise as FranchiseJson, Airing as AiringJson } from '../franchise/types';

export default class AiringEventListing extends BaseEventListing {
  _franchise: FranchiseJson;
  _airing: AiringJson;
  modelType: string;

  constructor(airing: AiringJson, franchise: FranchiseJson) {
    super(airing, franchise);
    this._airing = airing?.modelType === 'AiringEventListing' ? airing._airing : airing;
    this._franchise = franchise;
    this.modelType = 'AiringEventListing';
  }

  get source() {
    return this._airing?.source;
  }

  get kind() {
    return this._airing?.kind;
  }

  get onNow() {
    const { start_date_time, end_date_time } = this._airing;

    return moment().isBetween(start_date_time, end_date_time);
  }

  get isNew() {
    return this._airing.is_new || false;
  }

  get recordable() {
    return this._airing.airing_type !== 'linear';
  }

  get isSports() {
    return this._airing.is_sports;
  }

  get sportTeamHomeId() {
    return this.isSports && this._airing.normalized_sport_team_home_id;
  }

  get transferable() {
    return false; // the same value for all instances
  }

  get watchNow() {
    return this.onNow;
  }

  get id() {
    return Object.values(this.airingData).join('-');
  }

  get airingId() {
    return this._airing.id;
  }

  get airingData() {
    return {
      channel: this.channel,
      start_time: this.startTime,
      duration: this.duration,
      upid: this.upid,
      egid: this.egidForSge,
    };
  }

  get upid() {
    return this._airing.upid || '';
  }

  get genres() {
    return this._airing.genres || [];
  }

  get duration() {
    if (String(this._airing.run_time).length > 3) {
      return Math.floor(this._airing.run_time / 60);
    }
    return this._airing.run_time || 0;
  }

  get formattedDuration() {
    const hours = Math.floor(this._airing.run_time / 3600);
    const minutes = Math.floor(this._airing.run_time / 60);
    if (hours > 0) {
      return hours + 'h' || '';
    }
    if (0 < minutes < 60) {
      return minutes + 'm' || '';
    }
    return hours + 'h' + minutes + 'm';
  }

  get dynaStream() {
    return this._listing;
  }

  get echostarId() {
    return this._airing?.content_identification.echostar_id;
  }

  get episodeNumber() {
    return this._airing.episode_number || 0;
  }

  get airingType() {
    return this._airing.airing_type || '';
  }

  get startDateTime() {
    return this._airing.start_date_time || '';
  }

  get endDateTime() {
    return this._airing.end_date_time || '';
  }

  get formattedStartDateTime() {
    const date = moment(this._airing.start_date_time).format('MMMM D, YYYY');
    const time = moment(this._airing.start_date_time).format('h:mma');
    return `${date} at ${time}`;
  }

  get formattedStartDate() {
    return moment(this._airing.start_date_time).format('dddd, MMMM D');
  }

  get startTime() {
    return this._airing.start_date_time;
  }

  get formattedStartTime() {
    return moment(this.startTime).format('h:mma');
  }

  get ordinalStartTime() {
    return moment
      .utc(this.startTime)
      .local()
      .format('dddd, MMMM Do');
  }

  get formattedTimeWindow() {
    const startTime = moment(this._airing.start_date_time).format('h:mm');
    const endTime = moment(this._airing.end_date_time).format('h:mma');

    return `${startTime} - ${endTime}`;
  }

  get formattedAiringPanelTime() {
    const startTime = moment(this._airing.start_date_time).format('h:mm');
    const endTime = moment(this._airing.end_date_time).format('h:mma');
    const preString = `${startTime} - ${endTime}`;
    const postString = preString.substr(0, preString.length - 1).replace(/:00/g, '');
    return postString;
  }

  get fullDesignTeamFormattedDateTimeTBDV1() {
    const startTime = moment(this._airing.start_date_time).format('dddd, MMMM D h:mma');
    return startTime;
  }

  // an alias for seasonNumber
  // added for conveniency, because all other listing types have a property with this name
  get season() {
    return this.seasonNumber;
  }

  get seasonNumber() {
    return this._airing.season_number || 'N/A';
  }

  get type() {
    return this._airing.kind || '';
  }

  get callsign() {
    return this._airing.channel_information?.callsign || this._airing.callsign;
  }

  get isHd() {
    return this._airing.is_hd;
  }

  get isHybrid() {
    return this._airing.airing_type === 'hybrid';
  }

  get image() {
    return this._airing?.images?.poster_url || this._airing?.images?.wide_poster;
  }

  get major() {
    return this._airing.major;
  }

  get minor() {
    return this._airing.minor;
  }

  get mappedDown() {
    return this._airing.mapped_down;
  }

  get name() {
    return this._airing.name;
  }

  get padded() {
    return this._airing.padded;
  }

  get serviceUniqueId() {
    return this._airing.service_unique_id;
  }

  get channelInformation() {
    if (this._airing.channel_information) {
      return this._airing.channel_information;
    }
    // TODO This can probably be removed. Left in for fallback/legacy cases.
    return {
      callsign: this.callsign,
      channel: this.channel,
      hd: this.isHd,
      image_url: this.image,
      major: this.major,
      mapped_down: this.mappedDown,
      minor: this.minor,
      padded: this.padded,
      service_unique_id: this.serviceUniqueId,
    };
  }

  get channel() {
    return this._airing.channel_information?.channel || this._airing.channel;
  }

  get channelMajor() {
    return this._airing.channel_information?.major;
  }

  get channelImage() {
    return this._airing.channel_information?.channel_image;
  }

  get channelPadded() {
    return this._airing.channel_information?.padded;
  }

  get contentIdentification() {
    return this._airing?.content_identification || {};
  }

  get description() {
    return this._airing.episode_description || this._airing.description || '';
  }

  get title() {
    return this._airing.episode_title || '';
  }

  get franchiseId() {
    return this._airing.franchise_id || '';
  }

  get franchiseSlug() {
    return this._airing.franchise_slug || ';';
  }

  get franchise() {
    return this._franchise;
  }

  get slug() {
    return this._airing.slug || '';
  }

  get timestamp() {
    // airings do not contain timestamp info becuase they are future airings. Returns 0 to indicate it hasn't started.
    if (this._airing) return 0;
    return 0;
  }

  get timerSet() {
    if (this._airing.timers) {
      return this._airing.timers.length > 0;
    }
    return false;
  }

  get timers() {
    return this._airing.timers || [];
  }

  get egidForSge() {
    return this._airing.egid_for_sge;
  }

  get rating() {
    return this._airing.tv_rating || this._airing.rating || 'N/A';
  }

  // No drm currently available for airing, uncomment when available.
  // get drm() {
  //   return this._airing.drm;
  // }
}
